import type { ComponentType, ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import type { Dashboard, DashboardAlert, DashboardOrder, DashboardStat } from '@/api/dashboard'
import { dashboardApi, isSupplierDashboard } from '@/api/dashboard'
import { ordersApi } from '@/api/orders'
import { AppShell } from '@/components/AppShell'
import {
  IconAlert,
  IconCart,
  IconChevronRight,
  IconClipboard,
  IconClock,
  IconInbox,
  IconLink,
  IconList,
  IconRepeat,
  IconStore,
  IconTruck,
  IconUsers,
} from '@/components/icons'
import { Card, EmptyState, SectionTitle, Skeleton, SkeletonList } from '@/components/ui'
import { StatusBadge } from '@/features/orders/StatusBadge'
import { formatDate, formatDateTime, formatRelativeDay } from '@/lib/format'
import { toast } from '@/lib/toast'

/** Το εικονίδιο κάθε μετρητή. Χωρίς αυτό οι κάρτες είναι τέσσερα ίδια νούμερα. */
const STAT_ICONS: Record<string, ComponentType<{ size?: number; className?: string }>> = {
  deliveries_today: IconTruck,
  awaiting_confirmation: IconClock,
  open_orders: IconClipboard,
  suppliers: IconStore,
  picking_today: IconClipboard,
  needs_response: IconInbox,
  customers: IconUsers,
  out_of_stock: IconAlert,
}

/** Ποιοι μετρητές αξίζουν τονισμό όταν δεν είναι μηδέν. */
const STAT_ACCENT: Record<string, 'brand' | 'accent' | 'amber'> = {
  deliveries_today: 'brand',
  awaiting_confirmation: 'accent',
  needs_response: 'accent',
  out_of_stock: 'amber',
  picking_today: 'brand',
}

export default function DashboardPage() {
  const { t } = useTranslation()

  const dashboard = useQuery({ queryKey: ['dashboard'], queryFn: dashboardApi.get })
  const data = dashboard.data

  // Ο χαιρετισμός αλλάζει με την ώρα. Ο κλάδος δουλεύει από τα ξημερώματα ως
  // αργά, και ένα «καλημέρα» στις έντεκα το βράδυ ξενίζει.
  const hour = new Date().getHours()
  const greeting = t(hour < 5 || hour >= 17 ? 'dashboard.greetingEvening' : 'dashboard.greeting', {
    name: data?.greetingName ?? '',
  })

  return (
    <AppShell title={t('nav.dashboard')} subtitle={data ? greeting : undefined}>
      {dashboard.isPending && <DashboardSkeleton />}

      {data && (
        <>
          {data.alerts.length > 0 && (
            <ul className="mb-4 flex flex-col gap-2">
              {data.alerts.map((alert) => (
                <li key={alert.kind}>
                  <AlertRow alert={alert} />
                </li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {data.stats.map((stat) => (
              <StatCard key={stat.key} stat={stat} />
            ))}
          </div>

          <SectionTitle>{t('dashboard.quickActions')}</SectionTitle>
          <div className="flex flex-col gap-2">
            {isSupplierDashboard(data) ? (
              <SupplierActions data={data} />
            ) : (
              <BusinessActions data={data} />
            )}
          </div>

          <SectionTitle
            action={
              <Link to="/orders" className="text-xs font-semibold text-brand-700 hover:underline">
                {t('dashboard.allOrders')}
              </Link>
            }
          >
            {t('dashboard.recent')}
          </SectionTitle>
          <RecentOrders orders={data.recentOrders} supplierSide={isSupplierDashboard(data)} />
        </>
      )}
    </AppShell>
  )
}

// --- Δομικά μέρη -------------------------------------------------------------

function StatCard({ stat }: { stat: DashboardStat }) {
  const { t } = useTranslation()
  const Icon = STAT_ICONS[stat.key] ?? IconClipboard
  // Ο τονισμός μπαίνει μόνο όταν το νούμερο σημαίνει κάτι: τέσσερα χρωματιστά
  // μηδενικά είναι θόρυβος.
  const accent = stat.value > 0 ? (STAT_ACCENT[stat.key] ?? null) : null

  const body = (
    <Card accent={accent} className="h-full p-4 transition-shadow hover:shadow-lift">
      <Icon size={20} className={accent ? 'text-brand-600' : 'text-gray-400'} />
      <p className="mt-2 text-3xl font-bold tabular-nums leading-none text-gray-900">{stat.value}</p>
      <p className="mt-1.5 text-sm leading-tight text-gray-500">{t(`dashboard.stats.${stat.key}`)}</p>
    </Card>
  )

  return stat.url ? (
    <Link to={stat.url} className="block">
      {body}
    </Link>
  ) : (
    body
  )
}

function AlertRow({ alert }: { alert: DashboardAlert }) {
  const { t } = useTranslation()
  const warning = alert.severity === 'warning'

  const label = t(`dashboard.alerts.${alert.kind}`, {
    count: alert.count,
    defaultValue: alert.message,
  })

  const inner = (
    <div
      className={
        'flex items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium ' +
        (warning
          ? 'border-amber-200 bg-amber-50 text-amber-900'
          : 'border-brand-200 bg-brand-50 text-brand-900')
      }
    >
      <IconAlert size={18} className="shrink-0" />
      <span className="min-w-0 flex-1">
        {label}
        {alert.message && alert.kind === 'cutoff_soon' && (
          <span className="block text-xs font-normal opacity-80">{alert.message}</span>
        )}
      </span>
      {alert.url && <IconChevronRight size={16} className="shrink-0 opacity-60" />}
    </div>
  )

  return alert.url ? <Link to={alert.url}>{inner}</Link> : inner
}

function ActionTile({
  to,
  onClick,
  busy = false,
  icon,
  label,
  hint,
  tone = 'default',
}: {
  to?: string
  onClick?: () => void
  busy?: boolean
  icon: ReactNode
  label: string
  hint?: string
  tone?: 'default' | 'primary'
}) {
  const className =
    'flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left shadow-card transition-shadow hover:shadow-lift disabled:opacity-60 ' +
    (tone === 'primary'
      ? 'bg-brand-700 text-white'
      : 'border border-gray-200/80 bg-white text-gray-900')

  const inner = (
    <>
      <span
        className={
          'flex size-10 shrink-0 items-center justify-center rounded-xl ' +
          (tone === 'primary' ? 'bg-white/15 text-white' : 'bg-brand-50 text-brand-600')
        }
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold">{label}</span>
        {hint && (
          <span
            className={
              'block truncate text-xs ' + (tone === 'primary' ? 'text-brand-100' : 'text-gray-500')
            }
          >
            {hint}
          </span>
        )}
      </span>
      <IconChevronRight size={18} className="shrink-0 opacity-50" />
    </>
  )

  // Το ίδιο πλακίδιο είτε οδηγεί σε οθόνη είτε εκτελεί ενέργεια: ο χρήστης
  // δεν χρειάζεται να ξεχωρίζει «σύνδεσμο» από «κουμπί».
  if (to) {
    return (
      <Link to={to} className={className}>
        {inner}
      </Link>
    )
  }
  return (
    <button type="button" onClick={onClick} disabled={busy} className={className}>
      {inner}
    </button>
  )
}

function BusinessActions({ data }: { data: Extract<Dashboard, { cartItemCount: number }> }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Η πιο συχνή κίνηση στην εστίαση είναι «ξαναστείλε ό,τι έστειλα»: ένα
  // πάτημα από την Αρχική, χωρίς να ψάξει κανείς την παραγγελία στο ιστορικό.
  const last = data.recentOrders.find((order) => order.status !== 'cancelled') ?? null
  const repeat = useMutation({
    mutationFn: (id: string) => ordersApi.duplicate(id),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['cart'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(t('orders.duplicatedToast', { count: result.addedItems }))
      navigate('/cart')
    },
  })

  return (
    <>
      {data.cartItemCount > 0 ? (
        <ActionTile
          to="/cart"
          tone="primary"
          icon={<IconCart size={20} />}
          label={t('dashboard.openCart', { count: data.cartItemCount })}
          hint={t('cart.willSend', { count: data.cartOrderCount })}
        />
      ) : (
        <ActionTile
          to="/list"
          tone="primary"
          icon={<IconList size={20} />}
          label={t('dashboard.newOrder')}
          hint={t('dashboard.newOrderHint')}
        />
      )}

      {last && (
        <ActionTile
          onClick={() => repeat.mutate(last.id)}
          busy={repeat.isPending}
          icon={<IconRepeat size={20} />}
          label={t('dashboard.repeatLast')}
          hint={`${last.supplierName} · ${formatRelativeDay(last.createdAt, t)}`}
        />
      )}

      <ActionTile
        to="/templates"
        icon={<IconRepeat size={20} />}
        label={t('dashboard.templates')}
        hint={
          data.nextRecurringDate && data.nextRecurringName
            ? t('dashboard.nextRecurring', {
                name: data.nextRecurringName,
                date: formatDate(data.nextRecurringDate),
              })
            : undefined
        }
      />
    </>
  )
}

function SupplierActions({ data }: { data: Extract<Dashboard, { pickingOrderCount: number }> }) {
  const { t } = useTranslation()

  return (
    <>
      <ActionTile
        to="/supplier/picking-list"
        tone="primary"
        icon={<IconClipboard size={20} />}
        label={t('dashboard.pickingList')}
        hint={
          data.pickingOrderCount > 0
            ? t('dashboard.pickingSummary', { count: data.pickingItemCount })
            : undefined
        }
      />
      <ActionTile to="/supplier/catalog" icon={<IconStore size={20} />} label={t('dashboard.myCatalog')} />
      <ActionTile to="/supplier/customers" icon={<IconLink size={20} />} label={t('portal.invite')} />
    </>
  )
}

function RecentOrders({
  orders,
  supplierSide,
}: {
  orders: DashboardOrder[]
  supplierSide: boolean
}) {
  const { t } = useTranslation()

  if (orders.length === 0) {
    return (
      <EmptyState
        icon={<IconClipboard size={26} />}
        title={t(supplierSide ? 'dashboard.noRecentSupplier' : 'dashboard.noRecent')}
        description={t(
          supplierSide ? 'dashboard.noRecentSupplierHint' : 'dashboard.noRecentHint',
        )}
      />
    )
  }

  return (
    <ul className="flex flex-col gap-2">
      {orders.map((order) => (
        <li key={order.id}>
          <Link to={`/orders/${order.id}`} className="block">
            <Card className="flex items-center gap-3 p-4 transition-shadow hover:shadow-lift">
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-gray-900">{order.supplierName}</p>
                <p className="truncate text-xs text-gray-500">
                  <span className="font-mono">{order.orderNumber}</span> ·{' '}
                  {t('checkout.itemCount', { count: order.itemCount })} ·{' '}
                  {formatDateTime(order.createdAt)}
                </p>
              </div>
              <StatusBadge status={order.status} />
            </Card>
          </Link>
        </li>
      ))}
    </ul>
  )
}

/** Ο σκελετός έχει το ίδιο σχήμα με το περιεχόμενο, ώστε να μην αναπηδά τίποτα. */
function DashboardSkeleton() {
  return (
    <div aria-hidden>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="rounded-2xl border border-gray-200/80 bg-white p-4 shadow-card">
            <Skeleton className="size-5 rounded-md" />
            <Skeleton className="mt-2 h-8 w-12" />
            <Skeleton className="mt-2 h-3 w-20" />
          </div>
        ))}
      </div>
      <div className="mb-2 mt-5">
        <Skeleton className="h-3 w-28" />
      </div>
      <SkeletonList rows={2} />
    </div>
  )
}
